import * as THREE from 'three';
import type { SiteManifest } from './SiteManifest';
import { GeoWatcher } from './GeoWatcher';
import { OrientationSource } from './OrientationSource';
import { SceneRunner } from './SceneRunner';
import type { PoseProvider } from '../pose/PoseProvider';
import { XrPoseProvider } from '../pose/XrPoseProvider';
import { SensorPoseProvider } from '../pose/SensorPoseProvider';
import { DesktopPoseProvider } from '../pose/DesktopPoseProvider';
import { createRenderer } from '../render/Renderer';
import { CameraBackground } from '../render/CameraBackground';
import { DepthOcclusion } from '../render/DepthOcclusion';
import { IntroScreen, type AnchorChoice } from '../ui/IntroScreen';
import { TargetIndicator } from '../ui/TargetIndicator';
import { ScaleUI } from '../ui/ScaleUI';
import { computeViewAngle } from './ViewAngle';
import { CalibrationUI } from '../ui/CalibrationUI';
import { DebugHud } from '../ui/DebugHud';
import { GeofenceGuide } from '../ui/GeofenceGuide';
import type { GeoPoint } from './GeoMath';

export type PathKind = 'xr' | 'sensor' | 'desktop';

export interface AppOptions {
  container: HTMLElement;
  manifest: SiteManifest;
  baseUrl: string;
  path: PathKind;
  debug: boolean;
  /** `?geo=lat,lon` によるデスクトップ検証用の現在地固定 */
  geoOverride: GeoPoint | null;
  /** 起動時に事前取得した現在地(現在地アンカー用。取得失敗時はnull) */
  viewerOrigin: GeoPoint | null;
  /** 原点アンカーの選択チェックボックス(現在地アンカー対応史跡のみ非null) */
  anchorChoice: AnchorChoice | null;
}

const PATH_LABELS: Record<PathKind, string> = {
  xr: 'パスA: WebXR (ARCore)',
  sensor: 'パスB: GPS + 方位センサー',
  desktop: 'デスクトップ検証モード (ドラッグで視点回転 / WASDで移動)',
};

/** ライフサイクル統括(計画書§9) */
export class App {
  private opts: AppOptions;
  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private geo: GeoWatcher;
  private orientation: OrientationSource;
  private provider!: PoseProvider;
  private runner!: SceneRunner;
  private cameraBg = new CameraBackground();
  private depthOcclusion = new DepthOcclusion();
  private hud: DebugHud | null = null;
  private indicator: TargetIndicator | null = null;
  private loadPromise!: Promise<void>;
  private loadingBarFill: HTMLElement | null = null;
  private lastRenderAt = 0;

  constructor(opts: AppOptions) {
    this.opts = opts;
    this.geo = new GeoWatcher(opts.manifest.origin, opts.geoOverride);
    this.orientation = new OrientationSource();
  }

  init(): void {
    const { container, manifest, baseUrl, path, debug } = this.opts;

    const { renderer, scene, camera } = createRenderer(container);
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    // ポーズ供給源の選択 — 本設計の要(PoseProvider抽象)
    switch (path) {
      case 'xr':
        this.provider = new XrPoseProvider(
          renderer,
          this.geo,
          this.orientation,
          manifest.magneticDeclinationDeg,
          container,
        );
        break;
      case 'sensor':
        this.provider = new SensorPoseProvider(this.geo, this.orientation, manifest.magneticDeclinationDeg);
        break;
      case 'desktop':
        this.provider = new DesktopPoseProvider(renderer.domElement);
        scene.background = new THREE.Color(0x8fb6d9); // 仮の空
        break;
    }

    this.runner = new SceneRunner(manifest, renderer);
    scene.add(this.runner.contentGroup);

    if (debug) {
      this.hud = new DebugHud(container, this.geo, this.orientation, PATH_LABELS[path]);
      const grid = new THREE.GridHelper(500, 50, 0xffffff, 0x888888);
      (grid.material as THREE.Material).opacity = 0.25;
      (grid.material as THREE.Material).transparent = true;
      this.runner.contentGroup.add(grid);
    }

    // イントロ表示中にアセットのロードを開始しておく
    const loadingBar = document.createElement('div');
    loadingBar.className = 'loading-bar';
    loadingBar.innerHTML = '<div></div>';
    loadingBar.style.display = 'none';
    container.appendChild(loadingBar);
    this.loadingBarFill = loadingBar.firstElementChild as HTMLElement;

    this.loadPromise = this.runner
      .load(baseUrl, (done, total) => {
        if (this.loadingBarFill) this.loadingBarFill.style.width = `${(100 * done) / total}%`;
        if (done === total) loadingBar.style.display = 'none';
      })
      .catch((e: unknown) => {
        throw new Error(
          `コンテンツのロードに失敗しました: ${e instanceof Error ? e.message : String(e)}`,
        );
      });
    loadingBar.style.display = 'block';

    new IntroScreen(container, manifest, PATH_LABELS[path], this.opts.anchorChoice, (anchorFixed) =>
      this.start(anchorFixed),
    );
  }

  /**
   * 体験開始。IntroScreenのタップハンドラ内から呼ばれる。
   * iOS制限のため、権限リクエストとAudioContext.resume()をユーザージェスチャー起点で行う。
   * anchorFixed: チェックボックスの状態(true=史跡の実座標にアンカー)
   */
  private async start(anchorFixed: boolean): Promise<void> {
    const { container, manifest, path } = this.opts;

    // 現在地アンカー: チェックボックスがOFFなら事前取得済みの現在地を原点にする
    if (this.opts.anchorChoice && !anchorFixed) {
      const vo = this.opts.viewerOrigin;
      if (vo) {
        manifest.origin.lat = vo.lat;
        manifest.origin.lon = vo.lon;
        this.geo.setOrigin(vo);
        console.info(`[App] 原点を現在地 (${vo.lat.toFixed(6)}, ${vo.lon.toFixed(6)}) にアンカーしました`);
      } else {
        // GPS取得失敗: 座標なしでも「ユーザー=原点(着弾点)」として動作する固定位置モードで開始。
        // XRアライメントやセンサー系はENU(0,0)の即時サンプルを得られるため待ちが発生しない
        this.geo.useFixedPosition({ lat: manifest.origin.lat, lon: manifest.origin.lon });
        console.warn('[App] 現在地の取得に失敗。GPSなしフォールバック(今いる場所=着弾点)で開始します');
        this.showToast(
          container,
          '現在地を取得できないため、今いる場所を着弾点として体験を開始します。',
          undefined,
          10000,
        );
      }
    }

    if (path === 'xr') {
      // requestSessionはユーザーアクティベーション中に呼ぶ必要があるため最初に実行
      try {
        await this.provider.start();
      } catch (e) {
        // ARCore未対応・未インストール・拒否などでXRが開けない場合はパスBへ縮退
        console.warn('[App] WebXRセッションを開始できません。パスBへフォールバックします:', e);
        this.opts.path = 'sensor';
        this.provider = new SensorPoseProvider(
          this.geo,
          this.orientation,
          this.opts.manifest.magneticDeclinationDeg,
        );
        this.showToast(
          container,
          'WebXR(AR)を開始できないため、センサーモードで表示します。Android Chromeの場合は「Google Play開発者サービス(AR)」のインストールで改善することがあります。',
        );
        this.hud?.setMode(PATH_LABELS.sensor + ' (XRフォールバック)');
        await this.startSensorPath(container);
      }
    } else if (path === 'sensor') {
      await this.startSensorPath(container);
    } else {
      await this.provider.start();
    }

    // 体験の導入ガイド(実機パスのみ。機体は北西の空・仰角約54°から現れる)
    if (this.opts.path !== 'desktop') {
      this.showToast(container, 'スマホを真上に向け、空高く飛ぶ飛行機を探してください', undefined, 15000);
    }

    await this.loadPromise;
    await this.runner.start(); // AudioContext.resume() + ループ再生開始

    // 視野外ターゲットの方向矢印(B-29・模擬原爆を見失わないように)
    if (this.runner.indicatorTargets.length > 0) {
      this.indicator = new TargetIndicator(container, this.runner.indicatorTargets);
    }

    // 手動キャリブレーション(オフセットはlocalStorageに永続化)
    const toolbar = document.createElement('div');
    toolbar.className = 'toolbar';
    container.appendChild(toolbar);
    new CalibrationUI(container, toolbar, manifest.id, (deg) => this.provider.setYawOffsetDeg(deg));

    // 見かけの大きさ: 端末の画面視角から「肉眼と同じ視角」への補正係数を計算し、
    // その上にユーザー操作の拡大倍率(×1〜×20、永続化)を掛ける
    const view = computeViewAngle(this.camera.fov);
    const scaleUi = new ScaleUI(container, toolbar, manifest.id, view.screenAngleDeg, view.compensation, (mult) =>
      this.runner.setVisualScale(view.compensation * mult),
    );
    this.runner.setVisualScale(view.compensation * scaleUi.savedMult);
    console.info(
      `[App] 視角補正: 画面視角${view.screenAngleDeg.toFixed(1)}° → 補正×${view.compensation.toFixed(2)}、ユーザー倍率×${scaleUi.savedMult}`,
    );

    // デスクトップ検証(?geo指定)ではプロバイダがGeoWatcherを開始しないため、ここで開始する
    if (path === 'desktop' && this.opts.geoOverride) this.geo.start();

    // エリア外誘導(位置情報が使えるパスのみ)
    if (path !== 'desktop' || this.opts.geoOverride) {
      new GeofenceGuide(
        container,
        this.geo,
        this.orientation,
        manifest.origin,
        manifest.geofence.radiusMeters,
        manifest.title,
      );
    }

    // 深度オクルージョン(パスAのみ有効化されうる。非対応端末では自動無効)
    if (path === 'xr') {
      this.depthOcclusion.attachToGroup(this.runner.contentGroup);
    }

    // 非表示時は音声を止める(発熱・電池対策。rAFはブラウザが自動停止する)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) void this.runner.audio.suspend();
      else void this.runner.audio.resume();
    });

    if (this.opts.debug) {
      // デスクトップ検証・自動テスト用フック(?debug=1のときのみ)
      (window as unknown as Record<string, unknown>).__shiseki = {
        renderer: this.renderer,
        scene: this.scene,
        camera: this.camera,
        runner: this.runner,
        provider: this.provider,
        renderOnce: () => {
          this.provider.update(this.camera, this.runner.contentGroup);
          this.runner.update();
          this.indicator?.update(this.camera);
          this.renderer.render(this.scene, this.camera);
        },
      };
    }

    this.renderer.setAnimationLoop((time: number, frame?: XRFrame) => {
      // パスBは30fps上限(発熱・電池対策、計画書§11)。XRはランタイムに任せる
      if (this.opts.path === 'sensor') {
        if (time - this.lastRenderAt < 1000 / 30 - 1) return;
        this.lastRenderAt = time;
      }
      this.provider.update(this.camera, this.runner.contentGroup, frame);
      if (this.opts.path === 'xr') this.depthOcclusion.update(this.renderer, frame);
      this.runner.update();
      this.indicator?.update(this.camera);
      this.hud?.tick();
      if (this.hud) {
        this.hud.setExtra(
          `t: ${this.runner.audio.getTime().toFixed(2)}s / ${this.runner.loopSeconds}s` +
            `${this.runner.audio.loadedUrl ? '' : ' (無音クロック)'}` +
            `${this.opts.path === 'xr' ? `  depth: ${this.depthOcclusion.isActive ? 'ON' : 'OFF'}` : ''}` +
            `${this.opts.path === 'sensor' ? `  cam: ${this.cameraBg.active ? 'ON' : 'OFF'}` : ''}`,
        );
      }
      this.renderer.render(this.scene, this.camera);
    });
  }

  /** パスB開始: モーション権限 → カメラ背景(失敗時は再試行UI) → センサー購読 */
  private async startSensorPath(container: HTMLElement): Promise<void> {
    const granted = await this.orientation.requestPermission();
    if (!granted) {
      throw new Error('モーションセンサーの利用が許可されませんでした。ブラウザ設定から許可してください。');
    }
    await this.tryStartCameraBackground(container);
    await this.provider.start();
  }

  /** カメラ背景の開始。拒否・失敗時は再試行ボタン付きの通知を出す(体験自体は継続) */
  private async tryStartCameraBackground(container: HTMLElement): Promise<void> {
    try {
      await this.cameraBg.start(container);
    } catch (e) {
      console.warn('[App] カメラ背景を利用できません:', e);
      this.showToast(
        container,
        'カメラ映像を利用できません(背景なしで続行します)。ブラウザのサイト設定でカメラを「許可」にしてください。',
        {
          label: 'カメラを再試行',
          onClick: () => void this.tryStartCameraBackground(container),
        },
      );
    }
  }

  /** 画面上部の通知。actionでボタン表示、ttlMsで自動消滅。複数はスタック表示される */
  private showToast(
    container: HTMLElement,
    message: string,
    action?: { label: string; onClick: () => void },
    ttlMs?: number,
  ): void {
    let stack = container.querySelector('.toast-stack');
    if (!stack) {
      stack = document.createElement('div');
      stack.className = 'toast-stack';
      container.appendChild(stack);
    }
    const toast = document.createElement('div');
    toast.className = 'toast';
    const text = document.createElement('div');
    text.textContent = message;
    toast.appendChild(text);
    if (action) {
      const btn = document.createElement('button');
      btn.className = 'btn-small accent';
      btn.textContent = action.label;
      btn.addEventListener('click', () => {
        toast.remove();
        action.onClick();
      });
      toast.appendChild(btn);
    }
    const close = document.createElement('button');
    close.className = 'btn-small';
    close.textContent = '×';
    close.addEventListener('click', () => toast.remove());
    toast.appendChild(close);
    stack.appendChild(toast);
    if (ttlMs) {
      window.setTimeout(() => toast.remove(), ttlMs);
    }
  }

  dispose(): void {
    this.renderer.setAnimationLoop(null);
    this.provider.stop();
    this.cameraBg.stop();
    this.runner.dispose();
    this.renderer.dispose();
  }
}
